import { type ComponentProps, type ReactNode, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { Box, FormControl, InputLabel, ListItemIcon, ListItemText, MenuItem, Select, Tooltip } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { PopoverOrigin } from "@mui/material/Popover";

type SelectDropdownOption<TValue extends string> = TValue | { readonly value: TValue; readonly label: ReactNode };

type DropdownSelectVariant = NonNullable<ComponentProps<typeof FormControl>["variant"]>;

interface SelectDropdownCommonProps<TValue extends string> {
  readonly options: ReadonlyArray<SelectDropdownOption<TValue>>;
  readonly label?: string;
  readonly ariaLabel?: string;
  readonly disabled?: boolean;
  readonly fontSize?: string | number;
  readonly selectionColor?: string;
  readonly variant?: DropdownSelectVariant;
  readonly sx?: SxProps<Theme>;
  /** Menu `Paper` styles (portaled; use for `.MuiMenuItem-root`, not root `sx`). */
  readonly menuPaperSx?: SxProps<Theme>;
  /** When `true`, menu width follows list content, not the trigger width. @default false */
  readonly autoWidth?: boolean;
  readonly menuAnchorOrigin?: PopoverOrigin["horizontal"];
}

export type DropdownSelectProps<TValue extends string> =
  | (SelectDropdownCommonProps<TValue> & {
      readonly multiple?: false;
      readonly value: TValue;
      readonly onChange: (value: TValue) => void;
    })
  | (SelectDropdownCommonProps<TValue> & {
      readonly multiple: true;
      readonly value: readonly TValue[];
      readonly onChange: (value: readonly TValue[]) => void;
    });

function isOptionObject<TValue extends string>(opt: SelectDropdownOption<TValue>): opt is { readonly value: TValue; readonly label: ReactNode } {
  return typeof opt === "object" && opt !== null && "value" in opt;
}

function displayForSelectedValue<TValue extends string>(selected: TValue, optionList: ReadonlyArray<SelectDropdownOption<TValue>>): ReactNode {
  const match = optionList.find((opt) => (isOptionObject(opt) ? opt.value : opt) === selected);
  if (match === undefined) {
    return selected;
  }

  return isOptionObject(match) ? match.label : match;
}

function displayForMultipleSelection<TValue extends string>(
  selected: readonly TValue[],
  optionList: ReadonlyArray<SelectDropdownOption<TValue>>
): ReactNode {
  if (selected.length === 0) {
    return "";
  }

  return selected.map((id) => displayForSelectedValue(id, optionList)).join(" ; ");
}

function sxPropsToArray(sx: SxProps<Theme> | undefined): Array<SxProps<Theme>> {
  if (sx === undefined) {
    return [];
  }

  return Array.isArray(sx) ? [...sx] : [sx];
}

function plainLabelForOption<TValue extends string>(opt: SelectDropdownOption<TValue>): string {
  if (isOptionObject(opt)) {
    const { label, value } = opt;
    if (typeof label === "string" || typeof label === "number") {
      return String(label);
    }

    return value;
  }

  return opt;
}

function selectionTooltipPlainText<TValue extends string>(
  isMultiple: boolean,
  selected: TValue | readonly TValue[],
  optionList: ReadonlyArray<SelectDropdownOption<TValue>>
): string {
  if (isMultiple) {
    const arr = Array.isArray(selected) ? selected : [];
    return arr
      .map((id) => {
        const optMatch = optionList.find((o) => (isOptionObject(o) ? o.value : o) === id);
        return optMatch === undefined ? id : plainLabelForOption(optMatch);
      })
      .join(", ");
  }

  const selectedOpt = optionList.find((o) => (isOptionObject(o) ? o.value : o) === selected);
  return selectedOpt === undefined ? String(selected) : plainLabelForOption(selectedOpt);
}

interface EllipsisValueWithOptionalTooltipProps {
  readonly title: string;
  readonly fontSize: string | number;
  readonly color: string;
  readonly children: ReactNode;
}

function EllipsisValueWithOptionalTooltip(props: EllipsisValueWithOptionalTooltipProps) {
  const { title, fontSize, color, children } = props;
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) {
      return;
    }

    const measure = () => {
      setTruncated(el.scrollWidth > el.clientWidth + 1);
    };

    measure();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (ro !== null) {
      ro.observe(el);
    }

    return () => {
      ro?.disconnect();
    };
  }, [title, children]);

  const inner = (
    <Box
      ref={ref}
      component="span"
      sx={{
        display: "block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "100%",
        paddingInline: 0.5,
        color,
        fontSize,
        lineHeight: "inherit"
      }}
    >
      {children}
    </Box>
  );

  if (title.length === 0) {
    return inner;
  }

  const disableListeners = truncated === false;

  return (
    <Tooltip
      title={title}
      placement="top"
      disableHoverListener={disableListeners}
      disableFocusListener={disableListeners}
      disableTouchListener={disableListeners}
    >
      {inner}
    </Tooltip>
  );
}

const DISABLED_SX_COLOR = "action.disabled" as const;

const DropdownSelect = <TValue extends string>(props: DropdownSelectProps<TValue>) => {
  const isMultiple = props.multiple === true;
  const {
    options,
    label,
    ariaLabel,
    disabled,
    fontSize = "1rem",
    selectionColor = "text.primary",
    variant = "standard",
    sx: sxProp,
    menuPaperSx,
    autoWidth = false,
    menuAnchorOrigin = "center"
  } = props;

  const reactId = useId();
  const idSuffix = reactId.replaceAll(":", "");
  const labelId = `dropdown-select-label-${idSuffix}`;
  const selectId = `dropdown-select-${idSuffix}`;

  const menuProps = useMemo(() => {
    const basePaperSx: SxProps<Theme> = {
      "& .MuiMenuItem-root.Mui-selected": {
        color: selectionColor
      },
      "& .MuiMenuItem-root.Mui-selected.Mui-focusVisible": {
        color: selectionColor
      }
    };

    return {
      anchorOrigin: { vertical: "bottom" as const, horizontal: menuAnchorOrigin },
      transformOrigin: { vertical: "top" as const, horizontal: menuAnchorOrigin },
      slotProps: {
        paper: {
          sx: (menuPaperSx === undefined ? basePaperSx : ([basePaperSx, menuPaperSx] as const)) as SxProps<Theme>
        }
      }
    };
  }, [menuAnchorOrigin, menuPaperSx, selectionColor]);

  const trailingSx = sxPropsToArray(sxProp);

  const formSx = [...trailingSx] as ComponentProps<typeof FormControl>["sx"];

  const selectMergedSx = useMemo((): SxProps<Theme> => {
    const base: SxProps<Theme> = {
      "&& .MuiSelect-select": {
        color: selectionColor
      },
      "&& .MuiSelect-icon": {
        color: selectionColor
      },
      "&.Mui-disabled": {
        "&& .MuiSelect-select": {
          color: DISABLED_SX_COLOR,
          WebkitTextFillColor: (theme: Theme) => theme.palette.action.disabled
        },
        "&& .MuiSelect-icon": {
          color: DISABLED_SX_COLOR
        }
      }
    };
    if (sxProp === undefined) {
      return base;
    }

    return [base, sxProp] as SxProps<Theme>;
  }, [selectionColor, sxProp]);

  const ariaOnlyInputProps = typeof ariaLabel === "string" && label === undefined ? { "aria-label": ariaLabel } : undefined;

  const valueSxColor = disabled === true ? DISABLED_SX_COLOR : selectionColor;

  let selectedArray: TValue[] = [];
  if (isMultiple) {
    selectedArray = Array.isArray(props.value) ? [...props.value] : [];
  }

  const singleValue: TValue = isMultiple ? ("" as TValue) : props.value;

  const tooltipPlainText = useMemo(() => {
    if (props.multiple === true) {
      return selectionTooltipPlainText(true, props.value, options);
    }

    return selectionTooltipPlainText(false, props.value, options);
  }, [options, props.multiple, props.value]);

  return (
    <FormControl variant={variant} size="small" fullWidth sx={formSx} {...(disabled === undefined ? {} : { disabled })}>
      {label === undefined ? null : (
        <InputLabel id={labelId} variant={variant}>
          {label}
        </InputLabel>
      )}
      <Select<TValue>
        variant={variant}
        id={selectId}
        labelId={label === undefined ? undefined : labelId}
        label={label}
        multiple={isMultiple}
        value={(isMultiple ? selectedArray : singleValue) as TValue & TValue[]}
        displayEmpty
        autoWidth={autoWidth}
        MenuProps={menuProps}
        renderValue={(selected) => {
          if (isMultiple) {
            return (
              <EllipsisValueWithOptionalTooltip title={tooltipPlainText} fontSize={fontSize} color={valueSxColor}>
                {displayForMultipleSelection(selected as unknown as TValue[], options)}
              </EllipsisValueWithOptionalTooltip>
            );
          }

          return (
            <EllipsisValueWithOptionalTooltip title={tooltipPlainText} fontSize={fontSize} color={valueSxColor}>
              {displayForSelectedValue(selected as unknown as TValue, options)}
            </EllipsisValueWithOptionalTooltip>
          );
        }}
        onChange={(e: SelectChangeEvent<TValue | TValue[]>) => {
          if (isMultiple) {
            const raw = e.target.value;
            const arr = (typeof raw === "string" ? (raw.split(",") as TValue[]) : (raw as TValue[])) as TValue[];
            props.onChange(arr);
          } else {
            (props.onChange as (value: TValue) => void)(e.target.value as TValue);
          }
        }}
        inputProps={ariaOnlyInputProps}
        sx={selectMergedSx}
      >
        {options.map((opt) => {
          const optValue = isOptionObject(opt) ? opt.value : opt;
          const optLabel = isOptionObject(opt) ? opt.label : opt;
          const checked = isMultiple ? selectedArray.includes(optValue) : false;
          return (
            <MenuItem key={optValue} value={optValue}>
              {isMultiple ? (
                <>
                  <ListItemIcon sx={{ minWidth: 36 }}>{checked ? <CheckIcon fontSize="small" sx={{ color: selectionColor }} /> : null}</ListItemIcon>
                  <ListItemText primary={optLabel} slotProps={{ primary: { sx: { fontSize } } }} />
                </>
              ) : (
                optLabel
              )}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default DropdownSelect;
